/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import { IPayableInvoice } from '../helpers/invoice-data';
import { payInvoices } from '../helpers/pay-invoices';
import { IInvoiceErrorState } from '../invoices-list';

export interface IPayInvoiceData {
    invoice: IPayableInvoice;
}

export interface IPayInvoiceResources {
    payInvoiceButtonText: string;
    payInvoiceButtonAriaLabel: string;
}

export interface IPayInvoiceProps extends IComponentProps<IPayInvoiceData> {
    context: ICoreContext;
    className: string;

    resources: IPayInvoiceResources;

    onError?(errorState: IInvoiceErrorState): void;
}

interface IPayInvoiceComponent extends IComponent<IPayInvoiceProps> {
}

const onClick = async (_event: React.MouseEvent<HTMLElement>, props: IPayInvoiceProps, setDisabled: (disabled: boolean) => void): Promise<void> => {
    setDisabled(true);

    const propagateError = (errorState: IInvoiceErrorState): void => {
        if (props.onError) {
            props.onError(errorState);
        }
        setDisabled(false);
    };

    await payInvoices([props.data.invoice], props.context.actionContext, propagateError);
};

const PayInvoiceComponentActions = {
    onClick
};

/**
 * On click handler function.
 * @param props -Invoice props.
 * @param setDisabled -Set disabled.
 * @returns Pay invoice component action.
 */
const onClickHandler = (props: IPayInvoiceProps,
    setDisabled: React.Dispatch<React.SetStateAction<boolean>>) => async (event: React.MouseEvent<HTMLElement>) => {
    await PayInvoiceComponentActions.onClick(event, props, setDisabled);
};

const PayInvoice: React.FC<IPayInvoiceProps> = (props: IPayInvoiceProps): JSX.Element => {
    const [disabled, setDisabled] = useState(false);

    return (
        <Button
            className={props.className}
            aria-label={props.resources.payInvoiceButtonAriaLabel}
            onClick={onClickHandler(props, setDisabled)}
            disabled={!props.data.invoice.canBePaid || disabled}
        >
            {props.resources.payInvoiceButtonText}
        </Button>
    );
};

// @ts-expect-error
export const PayInvoiceComponent: React.FunctionComponent<IPayInvoiceProps> = msdyn365Commerce.createComponent<IPayInvoiceComponent>(
    'PayInvoiceComponent',
    { component: PayInvoice, ...PayInvoiceComponentActions }
);
